import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { withTranslationCustom } from 'lib/i18next'
import classNames from 'classnames'

import AppContext from './AppContext'
import Icon from 'components/Icon'
import StatusMessageLabel from './UI/StatusMessage/StatusMessageLabel/StatusMessageLabel'

import 'styles/status-message.pcss'

const StatusMessage = (
    {
        t,
        style,
        type,
        error,
        icon,
        iconSize,
        cssClass,
        title = null,
        fixed = false,
        children,
        alignTop,
        border = true,
        compact,
        padding = true,
        appContext,
        onMount,
        margin = true,
        vertical = false,
        iconProps = {},
        highlight,
        label,
        labels,
        noLabelMargin,
        loading,
    }) => {
    useEffect(() => {
        onMount && onMount()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    labels = label ? [label] : labels

    iconSize = compact && !iconSize
        ? 'l'
        : iconSize || 'xxl'

    const renderDebugMessage = () => {
        let renderError
        if (React.isValidElement(error)) {
            renderError = error
        } else if (error && error.toString) {
            renderError = error.toString()
        } else {
            renderError = 'Invalid error data.'
        }

        return (
            <div className="status-message__error-debug">
                <div className="status-message__error-debug__error">
                    {renderError}
                </div>
            </div>
        )
    }

    const renderTitle = () => {
        if (title === false) return null

        const titleContent = title || t('errorMessage.default.title')

        if (!error && !title) return null

        return (
            <h5 className="status-message__title">{titleContent}</h5>
        )
    }

    const render = () => {
        return (
            (
                <div
                    style={style}
                    className={classNames(
                        'status-message',
                        { 'status-message--align-top': alignTop },
                        { 'status-message--alert': error },
                        { [`status-message--${type}`]: type },
                        { 'status-message--fixed': fixed },
                        { 'status-message--borderless': !border },
                        { 'status-message--compact': compact },
                        { 'status-message--paddingless': !padding },
                        { 'status-message--marginless': !margin },
                        { 'status-message--vertical': vertical },
                        { 'status-message--highlight': highlight },
                        { 'status-message--has-labels': labels && labels.length > 0 },
                        { 'status-message--no-label-margin': noLabelMargin },
                        { loading: loading },
                        { [cssClass]: cssClass },
                    )}
                >
                    {(labels && labels.length > 0) && (
                        <div className="status-message__labels">
                            {labels.map((label, i) => (
                                <StatusMessageLabel key={i} text={label.text} {...(label.props || {})} />
                            ))}
                        </div>
                    )}
                    <div className="status-message__content-wrapper">
                        {icon && <Icon name={icon} size={iconSize} {...iconProps} />}

                        <div className="status-message__main">
                            {renderTitle()}
                            <div className="status-message__content">
                                {children || t('errorMessage.default.content')}
                            </div>
                        </div>
                    </div>

                    {!!error && renderDebugMessage()}
                </div>
            )
        )
    }

    return appContext ? (
        <AppContext context={appContext}>
            {render()}
        </AppContext>
    ) : render()
}

StatusMessage.propTypes = {
    type: PropTypes.string,
    highlight: PropTypes.bool,
    appContext: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    onMount: PropTypes.func,
    vertical: PropTypes.bool,
    padding: PropTypes.bool,
    icon: PropTypes.string,
    iconSize: PropTypes.string,
    iconProps: PropTypes.shape(Icon.propTypes),
    cssClass: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([false])]),
    border: PropTypes.bool,
    label: PropTypes.shape({
        text: PropTypes.string.isRequired,
        props: PropTypes.object,
    }),
    labels: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        props: PropTypes.object,
    })),
    noLabelMargin: PropTypes.bool,
    loading: PropTypes.bool,
}

export default withTranslationCustom('errors')(StatusMessage)
